import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../contexts/WalletContext';
import { useContract } from '../contexts/ContractContext';
import { useNetwork } from '../contexts/NetworkContext';
import { getContractByAddress, getSignerContractByAddress, formatAmount, parseAmount } from '../contracts/contract';
import { trustlineService } from '../services/trustline';
import './VaultUserFeatures.css';

const VaultUserFeatures: React.FC = () => {
  const { signer, account, provider } = useWallet();
  const { contractAddress } = useContract();
  const { currentNetwork } = useNetwork();

  const [vaultState, setVaultState] = useState({
    totalAssets: '0',
    totalShares: '0',
    assetAddress: '',
    assetSymbol: '',
    assetDecimals: 18,
    vaultName: '',
    vaultSymbol: '',
    frozen: false,
    trustlineOnDeposit: false,
    trustlineOnMint: false,
    trustlineOnWithdraw: false,
    trustlineOnRedeem: false,
  });

  const [userBalance, setUserBalance] = useState({
    shares: '0',
    assetsValue: '0',
    assetBalance: '0', // User's asset token balance in wallet
  });

  const [isAdmin, setIsAdmin] = useState(false);

  const [activeTab, setActiveTab] = useState<'deposit' | 'mint' | 'withdraw' | 'redeem'>('deposit');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [depositAmount, setDepositAmount] = useState('');
  const [mintShares, setMintShares] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [redeemShares, setRedeemShares] = useState('');

  // Preview states
  const [depositPreview, setDepositPreview] = useState<string | null>(null);
  const [mintPreview, setMintPreview] = useState<string | null>(null);
  const [withdrawPreview, setWithdrawPreview] = useState<string | null>(null);
  const [redeemPreview, setRedeemPreview] = useState<string | null>(null);

  const loadVaultState = useCallback(async () => {
    if (!provider || !contractAddress) return;

    try {
      const contract = getContractByAddress(provider, contractAddress);
      
      const [
        totalAssets,
        totalShares,
        assetAddress,
        vaultName,
        vaultSymbol,
        frozen,
        trustlineOnDeposit,
        trustlineOnMint,
        trustlineOnWithdraw,
        trustlineOnRedeem,
      ] = await Promise.all([
        contract.totalAssets(),
        contract.totalSupply(),
        contract.asset(),
        contract.name(),
        contract.symbol(),
        contract.frozen(),
        contract.trustlineOnDeposit(),
        contract.trustlineOnMint(),
        contract.trustlineOnWithdraw(),
        contract.trustlineOnRedeem(),
      ]);

      // Get asset symbol and decimals
      let assetSymbol = 'Token';
      let assetDecimals = 18;
      if (assetAddress && assetAddress !== ethers.ZeroAddress) {
        try {
          const assetContract = new ethers.Contract(
            assetAddress,
            ['function symbol() view returns (string)', 'function decimals() view returns (uint8)'],
            provider
          );
          [assetSymbol, assetDecimals] = await Promise.all([
            assetContract.symbol(),
            assetContract.decimals(),
          ]);
        } catch (err) {
          console.warn('Failed to fetch asset info:', err);
        }
      }

      setVaultState({
        totalAssets: totalAssets.toString(),
        totalShares: totalShares.toString(),
        assetAddress,
        assetSymbol,
        assetDecimals: Number(assetDecimals),
        vaultName,
        vaultSymbol,
        frozen,
        trustlineOnDeposit,
        trustlineOnMint,
        trustlineOnWithdraw,
        trustlineOnRedeem,
      });
    } catch (err: any) {
      console.error('Failed to load vault state:', err);
      setError('Failed to load vault state: ' + (err.message || 'Unknown error'));
    }
  }, [provider, contractAddress]);

  const loadUserBalance = useCallback(async () => {
    if (!provider || !contractAddress || !account) return;

    try {
      const contract = getContractByAddress(provider, contractAddress);
      const [shares, assetAddress] = await Promise.all([
        contract.balanceOf(account),
        contract.asset(),
      ]);
      const assetsValue = await contract.convertToAssets(shares);

      // Load user's asset token balance
      let assetBalance = '0';
      if (assetAddress && assetAddress !== ethers.ZeroAddress) {
        try {
          const assetContract = new ethers.Contract(
            assetAddress,
            ['function balanceOf(address) view returns (uint256)'],
            provider
          );
          const balance = await assetContract.balanceOf(account);
          assetBalance = balance.toString();
        } catch (err) {
          console.warn('Failed to fetch asset balance:', err);
        }
      }

      // Check if user has admin role
      try {
        const DEFAULT_ADMIN_ROLE = '0x0000000000000000000000000000000000000000000000000000000000000000';
        const hasAdminRole = await contract.hasRole(DEFAULT_ADMIN_ROLE, account);
        setIsAdmin(hasAdminRole);
      } catch (err) {
        console.warn('Failed to check admin role:', err);
        setIsAdmin(false);
      }

      setUserBalance({
        shares: shares.toString(),
        assetsValue: assetsValue.toString(),
        assetBalance,
      });
    } catch (err: any) {
      console.error('Failed to load user balance:', err);
    }
  }, [provider, contractAddress, account]);

  useEffect(() => {
    loadVaultState();
    loadUserBalance();
    const interval = setInterval(() => {
      loadVaultState();
      loadUserBalance();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [loadVaultState, loadUserBalance]);

  // Preview calculations
  const calculateDepositPreview = useCallback(async () => {
    if (!depositAmount || !provider || !contractAddress) {
      setDepositPreview(null);
      return;
    }

    try {
      const contract = getContractByAddress(provider, contractAddress);
      const shares = await contract.previewDeposit(parseAmount(depositAmount, vaultState.assetDecimals));
      setDepositPreview(formatAmount(shares.toString(), vaultState.assetDecimals));
    } catch (err) {
      setDepositPreview(null);
    }
  }, [depositAmount, provider, contractAddress, vaultState.assetDecimals]);

  const calculateMintPreview = useCallback(async () => {
    if (!mintShares || !provider || !contractAddress) {
      setMintPreview(null);
      return;
    }

    try {
      const contract = getContractByAddress(provider, contractAddress);
      const assets = await contract.previewMint(parseAmount(mintShares, vaultState.assetDecimals));
      setMintPreview(formatAmount(assets.toString(), vaultState.assetDecimals));
    } catch (err) {
      setMintPreview(null);
    }
  }, [mintShares, provider, contractAddress, vaultState.assetDecimals]);

  const calculateWithdrawPreview = useCallback(async () => {
    if (!withdrawAmount || !provider || !contractAddress) {
      setWithdrawPreview(null);
      return;
    }

    try {
      const contract = getContractByAddress(provider, contractAddress);
      const shares = await contract.previewWithdraw(parseAmount(withdrawAmount, vaultState.assetDecimals));
      setWithdrawPreview(formatAmount(shares.toString(), vaultState.assetDecimals));
    } catch (err) {
      setWithdrawPreview(null);
    }
  }, [withdrawAmount, provider, contractAddress, vaultState.assetDecimals]);

  const calculateRedeemPreview = useCallback(async () => {
    if (!redeemShares || !provider || !contractAddress) {
      setRedeemPreview(null);
      return;
    }

    try {
      const contract = getContractByAddress(provider, contractAddress);
      const assets = await contract.previewRedeem(parseAmount(redeemShares, vaultState.assetDecimals));
      setRedeemPreview(formatAmount(assets.toString(), vaultState.assetDecimals));
    } catch (err) {
      setRedeemPreview(null);
    }
  }, [redeemShares, provider, contractAddress, vaultState.assetDecimals]);

  useEffect(() => {
    calculateDepositPreview();
  }, [calculateDepositPreview]);

  useEffect(() => {
    calculateMintPreview();
  }, [calculateMintPreview]);

  useEffect(() => {
    calculateWithdrawPreview();
  }, [calculateWithdrawPreview]);

  useEffect(() => {
    calculateRedeemPreview();
  }, [calculateRedeemPreview]);

  // Helper function to ensure sufficient token allowance
  const ensureAllowance = useCallback(async (
    assetContract: ethers.Contract,
    account: string,
    contractAddress: string,
    requiredAmount: string
  ): Promise<void> => {
    let allowance = await assetContract.allowance(account, contractAddress);

    if (BigInt(allowance.toString()) < BigInt(requiredAmount)) {
      const approveTx = await assetContract.approve(contractAddress, requiredAmount);
      const receipt = await approveTx.wait();
      
      // Wait for the approval to be confirmed and state to propagate
      if (receipt && receipt.status === 1) {
        // Re-check allowance to ensure it's been updated
        let retries = 5;
        while (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5s
          allowance = await assetContract.allowance(account, contractAddress);
          if (BigInt(allowance.toString()) >= BigInt(requiredAmount)) {
            return; // Allowance confirmed, proceed
          }
          retries--;
        }
        
        if (BigInt(allowance.toString()) < BigInt(requiredAmount)) {
          throw new Error('Approval transaction confirmed but allowance not updated. Please try again.');
        }
      } else {
        throw new Error('Approval transaction failed.');
      }
    }
  }, []);

  const handleDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const assetContract = new ethers.Contract(
        vaultState.assetAddress,
        ['function approve(address spender, uint256 amount) returns (bool)', 'function allowance(address owner, address spender) view returns (uint256)'],
        signer
      );

      const amount = parseAmount(depositAmount, vaultState.assetDecimals);

      // Pre-validate with Trustline if enabled
      if (vaultState.trustlineOnDeposit) {
        const isValid = await trustlineService.validateVaultDeposit(
          currentNetwork.chainId,
          account,
          contractAddress,
          vaultState.assetAddress,
          amount,
          account
        );
        if (!isValid) {
          setError('Deposit failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      // Ensure sufficient allowance
      await ensureAllowance(assetContract, account, contractAddress, amount);

      const tx = await contract.deposit(amount, account);
      await tx.wait();

      setSuccess('Deposit successful!');
      setDepositAmount('');
      loadVaultState();
      loadUserBalance();
    } catch (err: any) {
      setError('Deposit failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const assetContract = new ethers.Contract(
        vaultState.assetAddress,
        ['function approve(address spender, uint256 amount) returns (bool)', 'function allowance(address owner, address spender) view returns (uint256)'],
        signer
      );

      const shares = parseAmount(mintShares, vaultState.assetDecimals);

      // Pre-validate with Trustline if enabled
      if (vaultState.trustlineOnMint) {
        const isValid = await trustlineService.validateVaultMint(
          currentNetwork.chainId,
          account,
          contractAddress,
          vaultState.assetAddress,
          shares,
          account
        );
        if (!isValid) {
          setError('Mint failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      const assetsNeeded = await contract.previewMint(shares);
      
      // Ensure sufficient allowance
      await ensureAllowance(assetContract, account, contractAddress, assetsNeeded.toString());

      const tx = await contract.mint(shares, account);
      await tx.wait();

      setSuccess('Mint successful!');
      setMintShares('');
      loadVaultState();
      loadUserBalance();
    } catch (err: any) {
      setError('Mint failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const amount = parseAmount(withdrawAmount, vaultState.assetDecimals);

      // Pre-validate with Trustline if enabled
      if (vaultState.trustlineOnWithdraw) {
        const isValid = await trustlineService.validateVaultWithdraw(
          currentNetwork.chainId,
          account,
          contractAddress,
          amount,
          account,
          account
        );
        if (!isValid) {
          setError('Withdraw failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      const tx = await contract.withdraw(amount, account, account);
      await tx.wait();

      setSuccess('Withdraw successful!');
      setWithdrawAmount('');
      loadVaultState();
      loadUserBalance();
    } catch (err: any) {
      setError('Withdraw failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const shares = parseAmount(redeemShares, vaultState.assetDecimals);

      // Pre-validate with Trustline if enabled
      if (vaultState.trustlineOnRedeem) {
        const isValid = await trustlineService.validateVaultRedeem(
          currentNetwork.chainId,
          account,
          contractAddress,
          shares,
          account,
          account
        );
        if (!isValid) {
          setError('Redeem failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      const tx = await contract.redeem(shares, account, account);
      await tx.wait();

      setSuccess('Redeem successful!');
      setRedeemShares('');
      loadVaultState();
      loadUserBalance();
    } catch (err: any) {
      setError('Redeem failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const exchangeRate = vaultState.totalShares !== '0' && vaultState.totalAssets !== '0'
    ? Number(formatAmount(vaultState.totalAssets, vaultState.assetDecimals)) / Number(formatAmount(vaultState.totalShares, vaultState.assetDecimals))
    : 1;

  // Balance validation helpers
  const hasInsufficientAssetBalance = useCallback((requiredAmount: string): boolean => {
    if (!requiredAmount || requiredAmount === '0' || !userBalance.assetBalance) return false;
    try {
      const required = BigInt(parseAmount(requiredAmount, vaultState.assetDecimals));
      const available = BigInt(userBalance.assetBalance);
      return required > available;
    } catch {
      return false;
    }
  }, [userBalance.assetBalance, vaultState.assetDecimals]);

  const hasInsufficientShareBalance = useCallback((requiredShares: string): boolean => {
    if (!requiredShares || requiredShares === '0' || !userBalance.shares) return false;
    try {
      const required = BigInt(parseAmount(requiredShares, vaultState.assetDecimals));
      const available = BigInt(userBalance.shares);
      return required > available;
    } catch {
      return false;
    }
  }, [userBalance.shares, vaultState.assetDecimals]);

  // Check if deposit amount exceeds asset balance
  const isDepositInsufficient = depositAmount ? hasInsufficientAssetBalance(depositAmount) : false;

  // Check if mint requires more assets than available
  const isMintInsufficient = useCallback(async (): Promise<boolean> => {
    if (!mintShares || !provider || !contractAddress) return false;
    try {
      const contract = getContractByAddress(provider, contractAddress);
      const assetsNeeded = await contract.previewMint(parseAmount(mintShares, vaultState.assetDecimals));
      return hasInsufficientAssetBalance(formatAmount(assetsNeeded.toString(), vaultState.assetDecimals));
    } catch {
      return false;
    }
  }, [mintShares, provider, contractAddress, vaultState.assetDecimals, hasInsufficientAssetBalance]);

  const [isMintInsufficientState, setIsMintInsufficientState] = useState(false);

  useEffect(() => {
    isMintInsufficient().then(setIsMintInsufficientState);
  }, [isMintInsufficient]);

  // Check if withdraw requires more shares than available
  const isWithdrawInsufficient = useCallback(async (): Promise<boolean> => {
    if (!withdrawAmount || !provider || !contractAddress) return false;
    try {
      const contract = getContractByAddress(provider, contractAddress);
      const sharesNeeded = await contract.previewWithdraw(parseAmount(withdrawAmount, vaultState.assetDecimals));
      return hasInsufficientShareBalance(formatAmount(sharesNeeded.toString(), vaultState.assetDecimals));
    } catch {
      return false;
    }
  }, [withdrawAmount, provider, contractAddress, vaultState.assetDecimals, hasInsufficientShareBalance]);

  const [isWithdrawInsufficientState, setIsWithdrawInsufficientState] = useState(false);

  useEffect(() => {
    isWithdrawInsufficient().then(setIsWithdrawInsufficientState);
  }, [isWithdrawInsufficient]);

  // Check if redeem amount exceeds share balance
  const isRedeemInsufficient = redeemShares ? hasInsufficientShareBalance(redeemShares) : false;

  return (
    <div className="vault-user-features">
      {/* Vault State Display */}
      <div className="vault-state-card">
        <h3 className="section-header">Vault Information</h3>
        <div className="vault-state-grid">
          <div className="vault-state-item">
            <span className="vault-state-label">Vault Name:</span>
            <span className="vault-state-value">{vaultState.vaultName || 'Loading...'}</span>
          </div>
          <div className="vault-state-item">
            <span className="vault-state-label">Asset:</span>
            <span className="vault-state-value">{vaultState.assetSymbol}</span>
          </div>
          <div className="vault-state-item">
            <span className="vault-state-label">Total Assets:</span>
            <span className="vault-state-value">
              {formatAmount(vaultState.totalAssets, vaultState.assetDecimals)} {vaultState.assetSymbol}
            </span>
          </div>
          <div className="vault-state-item">
            <span className="vault-state-label">Total Shares:</span>
            <span className="vault-state-value">
              {formatAmount(vaultState.totalShares, vaultState.assetDecimals)} {vaultState.vaultSymbol}
            </span>
          </div>
          <div className="vault-state-item">
            <span className="vault-state-label">Exchange Rate:</span>
            <span className="vault-state-value">
              1 {vaultState.vaultSymbol} = {exchangeRate.toFixed(6)} {vaultState.assetSymbol}
            </span>
          </div>
          <div className="vault-state-item">
            <span className="vault-state-label">Status:</span>
            <span className={`vault-state-value ${vaultState.frozen ? 'frozen' : 'active'}`}>
              {vaultState.frozen ? '❄️ Frozen' : '✅ Active'}
            </span>
          </div>
        </div>
      </div>

      {/* User Balance Display */}
      <div className="user-balance-card">
        <div className="user-balance-header">
          <h3 className="section-header">Your Position</h3>
          <span className={`role-badge ${isAdmin ? 'role-badge-manager' : 'role-badge-investor'}`}>
            {isAdmin ? 'Manager' : 'Investor'}
          </span>
        </div>
        <div className="user-balance-grid">
          <div className="user-balance-item">
            <span className="user-balance-label">Your Shares:</span>
            <span className="user-balance-value">
              {formatAmount(userBalance.shares, vaultState.assetDecimals)} {vaultState.vaultSymbol}
            </span>
          </div>
          <div className="user-balance-item">
            <span className="user-balance-label">Your Assets Value:</span>
            <span className="user-balance-value">
              {formatAmount(userBalance.assetsValue, vaultState.assetDecimals)} {vaultState.assetSymbol}
            </span>
          </div>
        </div>
        <div className="wallet-balance-hint">
          <span className="wallet-balance-label">Available in your wallet:</span>
          <span className="wallet-balance-value">
            {formatAmount(userBalance.assetBalance, vaultState.assetDecimals)} {vaultState.assetSymbol}
          </span>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Operation Tabs */}
      <div className="vault-operations-card">
        <div className="operation-tabs">
          <button
            className={`tab-button ${activeTab === 'deposit' ? 'active' : ''}`}
            onClick={() => setActiveTab('deposit')}
          >
            Deposit
          </button>
          <button
            className={`tab-button ${activeTab === 'mint' ? 'active' : ''}`}
            onClick={() => setActiveTab('mint')}
          >
            Mint
          </button>
          <button
            className={`tab-button ${activeTab === 'withdraw' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdraw')}
          >
            Withdraw
          </button>
          <button
            className={`tab-button ${activeTab === 'redeem' ? 'active' : ''}`}
            onClick={() => setActiveTab('redeem')}
          >
            Redeem
          </button>
        </div>

        {/* Deposit Form */}
        {activeTab === 'deposit' && (
          <form onSubmit={handleDeposit} className="vault-form">
            <div className="form-group">
              <label htmlFor="deposit-amount">Amount ({vaultState.assetSymbol}):</label>
              <input
                id="deposit-amount"
                type="text"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading || vaultState.frozen}
              />
              {depositPreview && (
                <div className="preview-text">
                  You will receive: {depositPreview} {vaultState.vaultSymbol}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !depositAmount || BigInt(depositAmount) === BigInt(0) || vaultState.frozen || isDepositInsufficient}
            >
              {loading ? 'Processing...' : vaultState.frozen ? 'Vault is Frozen' : isDepositInsufficient ? 'Insufficient Balance' : 'Deposit'}
            </button>
          </form>
        )}

        {/* Mint Form */}
        {activeTab === 'mint' && (
          <form onSubmit={handleMint} className="vault-form">
            <div className="form-group">
              <label htmlFor="mint-shares">Shares ({vaultState.vaultSymbol}):</label>
              <input
                id="mint-shares"
                type="text"
                value={mintShares}
                onChange={(e) => setMintShares(e.target.value)}
                placeholder="0.0"
                disabled={loading || vaultState.frozen}
              />
              {mintPreview && (
                <div className="preview-text">
                  You need: {mintPreview} {vaultState.assetSymbol}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !mintShares || BigInt(mintShares) === BigInt(0) || vaultState.frozen || isMintInsufficientState}
            >
              {loading ? 'Processing...' : vaultState.frozen ? 'Vault is Frozen' : isMintInsufficientState ? 'Insufficient Balance' : 'Mint'}
            </button>
          </form>
        )}

        {/* Withdraw Form */}
        {activeTab === 'withdraw' && (
          <form onSubmit={handleWithdraw} className="vault-form">
            <div className="form-group">
              <label htmlFor="withdraw-amount">Amount ({vaultState.assetSymbol}):</label>
              <input
                id="withdraw-amount"
                type="text"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading || vaultState.frozen}
              />
              {withdrawPreview && (
                <div className="preview-text">
                  You will burn: {withdrawPreview} {vaultState.vaultSymbol}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !withdrawAmount || BigInt(withdrawAmount) === BigInt(0) || vaultState.frozen || isWithdrawInsufficientState}
            >
              {loading ? 'Processing...' : vaultState.frozen ? 'Vault is Frozen' : isWithdrawInsufficientState ? 'Insufficient Balance' : 'Withdraw'}
            </button>
          </form>
        )}

        {/* Redeem Form */}
        {activeTab === 'redeem' && (
          <form onSubmit={handleRedeem} className="vault-form">
            <div className="form-group">
              <label htmlFor="redeem-shares">Shares ({vaultState.vaultSymbol}):</label>
              <input
                id="redeem-shares"
                type="text"
                value={redeemShares}
                onChange={(e) => setRedeemShares(e.target.value)}
                placeholder="0.0"
                disabled={loading || vaultState.frozen}
              />
              {redeemPreview && (
                <div className="preview-text">
                  You will receive: {redeemPreview} {vaultState.assetSymbol}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !redeemShares || BigInt(redeemShares) === BigInt(0) || vaultState.frozen || isRedeemInsufficient}
            >
              {loading ? 'Processing...' : vaultState.frozen ? 'Vault is Frozen' : isRedeemInsufficient ? 'Insufficient Balance' : 'Redeem'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default VaultUserFeatures;

